/** @format */
import { getConfig } from '@toucan/common/services/nextjs/settings';

import { IntercomCommand } from '@toucan/client/global/intercom';
import { MarketCode } from '@toucan/client/global/market';

import { GetHelpCentreUrlArgs } from './types';

/**
 * Get the help centre URL based on certain factors. These are, in order:
 *   - The `isConsolidatedIntercomConfigActive` argument, which determines the
 *     configuration to target
 *   - The `code` property of the `market` argument, when provided
 *   - The `marketCode` argument, when provided
 */
export const getHelpCentreUrl = ({
    market,
    marketCode,
    isConsolidatedIntercomConfigActive = false,
}: GetHelpCentreUrlArgs) => {
    const code = market?.code ?? marketCode;

    if (code) {
        return getHelpCentreUrlFromMarketCode(
            code,
            isConsolidatedIntercomConfigActive
        );
    }

    return '';
};

const getMarketLanguagePathFromCode = (marketCode: MarketCode) => {
    const { publicRuntimeConfig } = getConfig();

    const marketOverride =
        publicRuntimeConfig?.intercomConsolidatedLinkMarketOverrides?.[
            marketCode
        ];
    return (marketOverride ?? marketCode.split('-')[0]).toLowerCase();
};

const getHelpCentreUrlFromMarketCode = (
    marketCode: MarketCode,
    isConsolidatedIntercomConfigActive = false
): string => {
    const { publicRuntimeConfig } = getConfig();

    if (
        isConsolidatedIntercomConfigActive &&
        publicRuntimeConfig?.intercomConsolidatedLink
    ) {
        const languagePath = getMarketLanguagePathFromCode(marketCode);
        const baseUrl = new URL(
            languagePath,
            // Want to ensure this ends with a single trailing slash
            `${publicRuntimeConfig.intercomConsolidatedLink}/`.replace(
                /\/{2,}$/,
                '/'
            )
        );
        return baseUrl.href;
    }

    return publicRuntimeConfig?.intercomLinks?.[marketCode] ?? '';
};

export const executeIntercomCommand = (command: IntercomCommand): void => {
    switch (command.type) {
        case 'show':
        case 'hide':
        case 'showMessages':
            window.Intercom?.(command.type);
            break;
        case 'showConversation':
            window.Intercom?.('showConversation', command.conversationId);
            break;
        default:
            command satisfies never;
    }
};
